import {
  Datagrid,
  DateField,
  Edit,
  EditButton,
  Filter,
  List,
  NumberField,
  NumberInput,
  Pagination,
  ReferenceInput,
  Resource,
  SaveButton,
  SelectInput,
  Show,
  SimpleForm,
  SimpleShowLayout,
  TextField,
  TextInput,
  Toolbar,
  useListContext,
  useRecordContext,
} from "react-admin";
import {
  Link,
  useHistory,
} from "react-router-dom";
import {
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from "@material-ui/core";
import {
  BcomeVersionFilterInput,
  useBcomeVersionMap,
} from "../components/DbVersion";

// ─── Shared filter by article name ───────────────────────────────────────────

const ArticleFilter = (props) => (
  <Filter {...props}>
    <TextInput label="Search by article" source="article.name" alwaysOn />
    <ReferenceInput label="Project" source="projectId" reference="projects" alwaysOn>
      <SelectInput optionText="name" />
    </ReferenceInput>
  </Filter>
);

const ArticleLcaFilter = (props) => (
  <Filter {...props}>
    <TextInput label="Search by article" source="article.name" alwaysOn />
    <ReferenceInput label="Project" source="projectId" reference="projects" alwaysOn>
      <SelectInput optionText="name" />
    </ReferenceInput>
    <BcomeVersionFilterInput alwaysOn />
  </Filter>
);

// ─── LCA ─────────────────────────────────────────────────────────────────────

// Exclusion rule: only _U_A and _U_A_IS (no _P_, _SD, _R-SD, _UE, _PE)
const LCA_INDICATORS = [
  { key: "WS",     label: "Water Scarcity",                  unit: "m³ water eq"    },
  { key: "GW",     label: "Climate Change",                  unit: "kg CO₂ eq"      },
  { key: "Eutr",   label: "Freshwater eutrophication",       unit: "g P eq"         },
  { key: "AD",     label: "Fossil Fuels",                    unit: "MJ"             },
  { key: "OD",     label: "Ozone Depletion",                 unit: "kg CFC-11 eq"   },
  { key: "HTC",    label: "Human Toxicity Cancer",           unit: "CTUh"           },
  { key: "HTnC",   label: "Human Toxicity Non-Cancer",       unit: "CTUh"           },
  { key: "PM",     label: "Particulate Matter",              unit: "disease inc."   },
  { key: "IR",     label: "Ionizing Radiation",              unit: "kBq U-235 eq"   },
  { key: "POF",    label: "Photochem. Ozone Formation",      unit: "kg NMVOC eq"    },
  { key: "Acid",   label: "Acidification",                   unit: "mol H⁺ eq"      },
  { key: "TEutr",  label: "Terrestrial Eutrophication",      unit: "mol N eq"       },
  { key: "MEutr",  label: "Marine Eutrophication",           unit: "kg N eq"        },
  { key: "Ecotox", label: "Freshwater Ecotoxicity",          unit: "CTUe"           },
  { key: "LU",     label: "Land Use",                        unit: "Pt"             },
  { key: "ADMM",   label: "Resource Use Min. & Metals",      unit: "kg Sb eq"       },
  { key: "WC",     label: "Water Consumption",               unit: "liters water"   },
];

// Stages: value column then IS column for each
const LCA_STAGES = [
  { suffix: "U_A",      label: "Unitario"         },
  { suffix: "RME_U_A",  label: "Extraction"       },
  { suffix: "MP_U_A",   label: "Mat. Processing"  },
  { suffix: "MF_U_A",   label: "Mat. Finishing"   },
  { suffix: "AF_U_A",   label: "Art. Finishing"   },
  { suffix: "M_U_A",    label: "Manufacturing"    },
  { suffix: "TI_U_A",   label: "Transport"        },
  { suffix: "Pack_U_A", label: "Packaging"        },
];

const lcaField   = (k, s) => `${k}_${s}`;
const lcaFieldIS = (k, s) => `${k}_${s}_IS`;

// Format number — same helper as MaterialImpactResource
const formatNumber = (value) => {
  if (value === null || value === undefined) return '-';
  if (typeof value !== 'number') return value;
  if (Math.abs(value) < 0.001 && value !== 0) return value.toExponential(3);
  if (Math.abs(value) >= 1000000) return value.toExponential(3);
  return value.toLocaleString('en-US', { maximumFractionDigits: 6, minimumFractionDigits: 0 });
};

const FormattedNumberField = ({ source, label }) => {
  const record = useRecordContext();
  const value = record?.[source];
  return (
    <TextField
      record={{ [source]: formatNumber(value) }}
      source={source}
      label={label}
    />
  );
};
FormattedNumberField.defaultProps = { addLabel: true };

const useLcaStyles = makeStyles({
  tableContainer: {
    marginTop: '15px',
    overflowX: 'auto',
  },
  table: {
    minWidth: 3500,
    borderCollapse: 'collapse',
  },
  headerGroup: {
    fontWeight: 'bold',
    textAlign: 'center',
    borderBottom: '1px solid #e0e0e0',
    borderLeft: '1px solid #e0e0e0',
    borderRight: '1px solid #e0e0e0',
    padding: '10px 8px',
    fontSize: '0.85rem',
  },
  headerSubGroup: {
    fontWeight: 600,
    textAlign: 'center',
    fontSize: '0.7rem',
    padding: '6px 2px',
    borderBottom: '1px solid #e0e0e0',
    whiteSpace: 'nowrap',
  },
  headerSubGroupFirst: {
    borderLeft: '1px solid #e0e0e0',
  },
  headerSubGroupLast: {
    borderRight: '1px solid #e0e0e0',
  },
  headerFixed: {
    fontWeight: 'bold',
    textAlign: 'center',
    borderBottom: '1px solid #e0e0e0',
    padding: '10px 8px',
  },
  cell: {
    textAlign: 'right',
    padding: '6px 4px',
    fontSize: '0.75rem',
    borderBottom: '1px solid #e0e0e0',
  },
  cellFirst: { borderLeft: '1px solid #e0e0e0' },
  cellLast:  { borderRight: '1px solid #e0e0e0' },
  cellLeft: {
    textAlign: 'left',
    padding: '6px 8px',
    fontSize: '0.8rem',
    backgroundColor: '#fff',
  },
  clickableRow: {
    cursor: 'pointer',
    '&:hover td': { backgroundColor: '#f0f0f0 !important' },
  },
});

// Each indicator: all article values first, then all Industry Standards
const LCA_SUB_COLUMNS = [
  ...LCA_STAGES.map(st => ({ stageSuffix: st.suffix, label: st.label, isIS: false })),
  ...LCA_STAGES.map(st => ({ stageSuffix: st.suffix, label: `${st.label} IS`, isIS: true }))
];

const LCA_FIXED_COLUMNS = [
  { source: "id",                label: "ID",                minWidth: 80  },
  { source: "article.name",     label: "Article",           minWidth: 200 },
  { source: "article.reference", label: "Article Reference", minWidth: 150 },
  { source: "project.name",     label: "Project",           minWidth: 150 },
];

const LcaGroupedDatagrid = () => {
  const classes = useLcaStyles();
  const { data, ids, loaded, basePath, currentSort, setSort } = useListContext();
  const history = useHistory();
  const bcomeVersionMap = useBcomeVersionMap();

  if (!loaded) return <div>Loading...</div>;
  if (!ids || ids.length === 0) return <div>No results found</div>;

  const colCount = LCA_SUB_COLUMNS.length; // 16 per indicator

  const handleSort = (field) => {
    const order = currentSort.field === field && currentSort.order === "ASC" ? "DESC" : "ASC";
    setSort(field, order);
  };

  return (
    <TableContainer component={Paper} className={classes.tableContainer}>
      <Table className={classes.table} size="small">
        <TableHead>
          {/* Row 1: fixed cols + indicator group headers */}
          <TableRow>
            {LCA_FIXED_COLUMNS.map(col => (
              <TableCell key={col.source} className={classes.headerFixed} rowSpan={2} style={{ minWidth: col.minWidth }}>
                <TableSortLabel
                  active={currentSort.field === col.source}
                  direction={currentSort.field === col.source ? currentSort.order.toLowerCase() : "asc"}
                  onClick={() => handleSort(col.source)}
                >
                  {col.label}
                </TableSortLabel>
              </TableCell>
            ))}
            <TableCell className={classes.headerFixed} rowSpan={2} style={{ minWidth: 140 }}>
              Version Set
            </TableCell>
            <TableCell className={classes.headerFixed} rowSpan={2} style={{ minWidth: 110 }}>
              PEFCR (µPt)
            </TableCell>
            {LCA_INDICATORS.map(ind => (
              <TableCell
                key={ind.key}
                className={classes.headerGroup}
                colSpan={colCount}
                style={{ minWidth: colCount * 80 }}
              >
                {ind.label} ({ind.unit})
              </TableCell>
            ))}
            <TableCell className={classes.headerFixed} rowSpan={2} style={{ minWidth: 100 }}>
              <TableSortLabel
                active={currentSort.field === "createdAt"}
                direction={currentSort.field === "createdAt" ? currentSort.order.toLowerCase() : "asc"}
                onClick={() => handleSort("createdAt")}
              >
                Created
              </TableSortLabel>
            </TableCell>
            <TableCell className={classes.headerFixed} rowSpan={2} style={{ minWidth: 100 }}>
              <TableSortLabel
                active={currentSort.field === "updatedAt"}
                direction={currentSort.field === "updatedAt" ? currentSort.order.toLowerCase() : "asc"}
                onClick={() => handleSort("updatedAt")}
              >
                Updated
              </TableSortLabel>
            </TableCell>
            <TableCell className={classes.headerFixed} rowSpan={2} style={{ minWidth: 80 }}>Actions</TableCell>
          </TableRow>

          {/* Row 2: sub-column labels */}
          <TableRow>
            {LCA_INDICATORS.map(ind =>
              LCA_SUB_COLUMNS.map((sub, colIdx) => (
                <TableCell
                  key={`${ind.key}_${sub.stageSuffix}_${sub.isIS ? 'IS' : 'val'}`}
                  className={[
                    classes.headerSubGroup,
                    colIdx === 0 ? classes.headerSubGroupFirst : '',
                    colIdx === colCount - 1 ? classes.headerSubGroupLast : '',
                  ].join(' ')}
                >
                  {sub.label}
                </TableCell>
              ))
            )}
          </TableRow>
        </TableHead>

        <TableBody>
          {ids.map(id => {
            const record = data[id];
            if (!record) return null;
            return (
              <TableRow
                key={id}
                className={classes.clickableRow}
                onClick={() => history.push(`${basePath}/${id}/show`)}
              >
                <TableCell className={classes.cellLeft}>{record.id?.substring(0, 8)}...</TableCell>
                <TableCell className={classes.cellLeft}>{record.article?.name || '-'}</TableCell>
                <TableCell className={classes.cellLeft}>{record.article?.reference || '-'}</TableCell>
                <TableCell className={classes.cellLeft}>{record.project?.name || '-'}</TableCell>
                <TableCell className={classes.cellLeft}>
                  {bcomeVersionMap[record.project?.bcomeVersionId] || '-'}
                </TableCell>
                <TableCell className={classes.cellLeft}>
                  {formatNumber(record.PEF_Score_U_A)}
                </TableCell>

                {LCA_INDICATORS.map(ind =>
                  LCA_SUB_COLUMNS.map((sub, colIdx) => {
                    const fieldName = sub.isIS
                      ? lcaFieldIS(ind.key, sub.stageSuffix)
                      : lcaField(ind.key, sub.stageSuffix);
                    return (
                      <TableCell
                        key={fieldName}
                        className={[
                          classes.cell,
                          colIdx === 0 ? classes.cellFirst : '',
                          colIdx === colCount - 1 ? classes.cellLast : '',
                        ].join(' ')}
                      >
                        {formatNumber(record[fieldName])}
                      </TableCell>
                    );
                  })
                )}

                <TableCell className={classes.cellLeft}>
                  {record.createdAt ? new Date(record.createdAt).toLocaleDateString() : '-'}
                </TableCell>
                <TableCell className={classes.cellLeft}>
                  {record.updatedAt ? new Date(record.updatedAt).toLocaleDateString() : '-'}
                </TableCell>
                <TableCell className={classes.cellLeft}>
                  <Link to={`${basePath}/${id}`} onClick={e => e.stopPropagation()}>Edit</Link>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const ArticleLcaList = (props) => (
  <List
    {...props}
    filters={<ArticleLcaFilter />}
    pagination={<Pagination rowsPerPageOptions={[10, 25, 50, 100]} />}
    bulkActionButtons={false}
  >
    <LcaGroupedDatagrid />
  </List>
);

const ArticleLcaShow = (props) => (
  <Show {...props}>
    <SimpleShowLayout>
      <TextField source="id" label="ID" />
      <TextField label="Article" source="article.name" />
      <TextField label="Article Reference" source="article.reference" />
      <TextField label="Project" source="project.name" />
      <DateField source="createdAt" label="Creation date" />
      <DateField source="updatedAt" label="Update date" />
      <FormattedNumberField source="PEF_Score_U_A" label="PEFCR unitary (µPt)" />
      {LCA_INDICATORS.map(ind =>
        LCA_STAGES.map(st => [
          <FormattedNumberField key={lcaField(ind.key, st.suffix)}   source={lcaField(ind.key, st.suffix)}   label={`${ind.label} - ${st.label} (${ind.unit})`}   />,
          <FormattedNumberField key={lcaFieldIS(ind.key, st.suffix)} source={lcaFieldIS(ind.key, st.suffix)} label={`${ind.label} - ${st.label} IS (${ind.unit})`} />,
        ])
      )}
    </SimpleShowLayout>
  </Show>
);

const ArticleLcaEdit = (props) => (
  <Edit {...props}>
    <SimpleForm>
      {LCA_INDICATORS.map(ind =>
        LCA_STAGES.map(st => [
          <NumberInput key={lcaField(ind.key, st.suffix)}   source={lcaField(ind.key, st.suffix)}   label={`${ind.label} - ${st.label} (${ind.unit})`}    step="any" />,
          <NumberInput key={lcaFieldIS(ind.key, st.suffix)} source={lcaFieldIS(ind.key, st.suffix)} label={`${ind.label} - ${st.label} IS (${ind.unit})`}  step="any" />,
        ])
      )}
    </SimpleForm>
  </Edit>
);

// ─── Eco-Score ────────────────────────────────────────────────────────────────

const ECOSCORE_GROUPS  = ["Supplier", "Material", "Corporate"];
const ECOSCORE_METRICS = ["Planet", "People", "Transparency", "Circularity"];

const ArticleEcoscoreList = (props) => (
  <List
    {...props}
    filters={<ArticleFilter />}
    pagination={<Pagination rowsPerPageOptions={[10, 25, 50]} />}
    bulkActionButtons={false}
  >
    <Datagrid rowClick="show" bulkActionButtons={false}>
      <TextField source="id" />
      <TextField label="Article" source="article.name" />
      <TextField label="Article Reference" source="article.reference" />
      <TextField label="Project" source="project.name" />
      {ECOSCORE_GROUPS.map(g =>
        ECOSCORE_METRICS.map(m => (
          <TextField key={`${g}_${m}`} source={`${g}_${m}`} label={`${g} ${m}`} emptyText="—" />
        ))
      )}
      <EditButton />
    </Datagrid>
  </List>
);

const ArticleEcoscoreShow = (props) => (
  <Show {...props}>
    <SimpleShowLayout>
      <TextField source="id" />
      <TextField label="Article" source="article.name" />
      <TextField label="Article Reference" source="article.reference" />
      <TextField label="Project" source="project.name" />
      {ECOSCORE_GROUPS.map(g =>
        ECOSCORE_METRICS.map(m => (
          <TextField key={`${g}_${m}`} source={`${g}_${m}`} label={`${g} ${m}`} emptyText="—" />
        ))
      )}
    </SimpleShowLayout>
  </Show>
);

const ArticleEcoscoreEdit = (props) => (
  <Edit {...props}>
    <SimpleForm>
      {ECOSCORE_GROUPS.map(g =>
        ECOSCORE_METRICS.map(m => (
          <TextInput key={`${g}_${m}`} source={`${g}_${m}`} label={`${g} ${m}`} helperText={false} />
        ))
      )}
    </SimpleForm>
  </Edit>
);

// ─── Circularity ──────────────────────────────────────────────────────────────

// Los índices de circularidad (IT-3181) se guardan como fracción 0–1 en el
// registro de circularity y se muestran/editan como % (×100). repairability_score
// es un entero 0–6, se muestra crudo.
const circularityPercentFormat = (v) =>
  v === null || v === undefined || v === "" ? v : Math.round(Number(v) * 1e6) / 1e4;
const circularityPercentParse = (v) =>
  v === null || v === undefined || v === "" ? null : Number(v) / 100;

// readOnly (IT-3226): indicadores generados por el motor de cálculo de
// Circularity — se muestran en List/Show pero no se editan desde el admin.
const CIRCULARITY_FIELDS = [
  { source: "MCI_A",      label: "MCI", percent: true },
  { source: "Recycled_A", label: "Recycled (kg/unit)" },
  { source: "TU_Waste_A", label: "Unrecoverable waste (kg/unit)" },
  { source: "Utility_A",  label: "Utility multiplier" },
  // IT-3181: nuevos indicadores de circularidad (IT-3119).
  { source: "verified_recycled_content_declared",  label: "Verified Recycled Content (Declared)",  percent: true },
  { source: "verified_recycled_content_certified", label: "Verified Recycled Content (Certified)", percent: true },
  { source: "repairability_score",                 label: "Repairability Score" },
  { source: "recyclability_index",                 label: "Recyclability Index",   percent: true },
  { source: "eol_circularity_index",               label: "EoL Circularity Index", percent: true },
  { source: "durability",                          label: "Durability",            percent: true, readOnly: true },
  // IT-3226: indicadores intermedios del MCI (IT-3137/3138/3143), solo lectura.
  { source: "brand_use_enablement",                label: "Brand Use Enablement",  readOnly: true },
  { source: "calculationType",                     label: "Calculation Type",      text: true, readOnly: true },
];

const ArticleCircularityList = (props) => (
  <List
    {...props}
    filters={<ArticleFilter />}
    pagination={<Pagination rowsPerPageOptions={[10, 25, 50]} />}
    bulkActionButtons={false}
  >
    <Datagrid rowClick="show" bulkActionButtons={false}>
      <TextField source="id" />
      <TextField label="Article" source="article.name" />
      <TextField label="Article Reference" source="article.reference" />
      <TextField label="Project" source="project.name" />
      {CIRCULARITY_FIELDS.map(f => f.text ? (
        <TextField key={f.source} source={f.source} label={f.label} emptyText="—" />
      ) : (
        <NumberField
          key={f.source}
          source={f.source}
          label={f.label}
          emptyText="—"
          options={f.percent ? { style: "percent", maximumFractionDigits: 2 } : undefined}
        />
      ))}
      <EditButton />
    </Datagrid>
  </List>
);

const ArticleCircularityShow = (props) => (
  <Show {...props}>
    <SimpleShowLayout>
      <TextField source="id" />
      <TextField label="Article" source="article.name" />
      <TextField label="Article Reference" source="article.reference" />
      <TextField label="Project" source="project.name" />
      {CIRCULARITY_FIELDS.map(f => f.text ? (
        <TextField key={f.source} source={f.source} label={f.label} emptyText="—" />
      ) : (
        <NumberField
          key={f.source}
          source={f.source}
          label={f.label}
          emptyText="—"
          options={f.percent ? { style: "percent", maximumFractionDigits: 2 } : undefined}
        />
      ))}
    </SimpleShowLayout>
  </Show>
);

// Los registros de circularity se generan automáticamente al crear el artículo y
// no tienen gestión CRUD independiente — solo lectura y edición manual (IT-3225).
// Toolbar sin DeleteButton para no exponer un borrado que el modelo no contempla.
const CircularityEditToolbar = (props) => (
  <Toolbar {...props}>
    <SaveButton />
  </Toolbar>
);

const ArticleCircularityEdit = (props) => (
  <Edit {...props}>
    <SimpleForm toolbar={<CircularityEditToolbar />}>
      {CIRCULARITY_FIELDS.filter(f => !f.readOnly).map(f => (
        <NumberInput
          key={f.source}
          source={f.source}
          label={f.percent ? `${f.label} (%)` : f.label}
          helperText={false}
          format={f.percent ? circularityPercentFormat : undefined}
          parse={f.percent ? circularityPercentParse : undefined}
        />
      ))}
    </SimpleForm>
  </Edit>
);

// ─── Ecobalyse ────────────────────────────────────────────────────────────────

const ECOBALYSE_GENERAL = [
  { source: "ecobalyseScore",                label: "Score",                    isNum: true  },
  { source: "ecobalyseImpactStandardized",   label: "Impact Standardized",      isNum: true  },
  { source: "ecobalyseDurabilityFactor",     label: "Durability Factor",        isNum: true  },
  { source: "ecobalyseResultType",           label: "Result Type",              isNum: false },
  { source: "ecobalyseOutOfEuropeEOLFactor", label: "Out of Europe EOL Factor", isNum: true  },
];

const ECOBALYSE_IMPACTS = [
  "ecobalyseAcidification",
  "ecobalyseClimateChange",
  "ecobalyseFreshwaterEutrophication",
  "ecobalyseResourceUseFossils",
  "ecobalyseWaterUse",
  "ecobalyseHumanToxicityCancer",
  "ecobalyseHumanToxicityNonCancer",
  "ecobalyseIonizingRadiation",
  "ecobalyseLandUse",
  "ecobalyseResourceUseMineralsAndMetals",
  "ecobalyseOzoneDepletion",
  "ecobalysePhotochemicalOzoneFormation",
  "ecobalyseParticulateMatter",
  "ecobalyseMarineEutrophication",
  "ecobalyseTerrestrialEutrophication",
  "ecobalyseWaterDepletion",
  "ecobalyseMicrofibersRelease",
];

const ArticleEcobalyseList = (props) => (
  <List
    {...props}
    filters={<ArticleFilter />}
    pagination={<Pagination rowsPerPageOptions={[10, 25, 50]} />}
    bulkActionButtons={false}
  >
    <Datagrid rowClick="show" bulkActionButtons={false}>
      <TextField source="id" />
      <TextField label="Article" source="article.name" />
      <TextField label="Article Reference" source="article.reference" />
      <TextField label="Project" source="project.name" />
      <NumberField source="ecobalyseScore"       label="Score"        emptyText="—" />
      <TextField   source="ecobalyseResultType"  label="Result Type"  emptyText="—" />
      <NumberField source="ecobalyseClimateChange" label="Climate Change" emptyText="—" />
      <EditButton />
    </Datagrid>
  </List>
);

const ArticleEcobalyseShow = (props) => (
  <Show {...props}>
    <SimpleShowLayout>
      <TextField source="id" />
      <TextField label="Article" source="article.name" />
      <TextField label="Article Reference" source="article.reference" />
      <TextField label="Project" source="project.name" />
      <TextField source="ecobalyseSVG" label="SVG" />
      {ECOBALYSE_GENERAL.map(f =>
        f.isNum
          ? <NumberField key={f.source} source={f.source} label={f.label} emptyText="—" />
          : <TextField   key={f.source} source={f.source} label={f.label} emptyText="—" />
      )}
      {ECOBALYSE_IMPACTS.map(s => (
        <NumberField key={s} source={s} label={s} emptyText="—" />
      ))}
    </SimpleShowLayout>
  </Show>
);

const ArticleEcobalyseEdit = (props) => (
  <Edit {...props}>
    <SimpleForm>
      <TextInput source="ecobalyseSVG" label="ecobalyseSVG" multiline helperText={false} />
      {ECOBALYSE_GENERAL.map(f =>
        f.isNum
          ? <NumberInput key={f.source} source={f.source} label={f.label} helperText={false} />
          : <TextInput   key={f.source} source={f.source} label={f.label} helperText={false} />
      )}
      {ECOBALYSE_IMPACTS.map(s => (
        <NumberInput key={s} source={s} label={s} helperText={false} />
      ))}
    </SimpleForm>
  </Edit>
);

// ─── Resource exports ─────────────────────────────────────────────────────────
// Resource names use camelCase (no slashes) so react-admin generates clean URLs.
// The dataProvider maps these names to the real API paths via resourceMapping.

export const articleImpactLcaResource = () => (
  <Resource
    name="articleImpactLca"
    options={{ label: "Article LCA", menuGroup: "Article Impact Sub" }}
    list={ArticleLcaList}
    show={ArticleLcaShow}
    edit={ArticleLcaEdit}
  />
);

export const articleImpactEcoscoreResource = () => (
  <Resource
    name="articleImpactEcoscore"
    options={{ label: "Article Eco-Score", menuGroup: "Article Impact Sub" }}
    list={ArticleEcoscoreList}
    show={ArticleEcoscoreShow}
    edit={ArticleEcoscoreEdit}
  />
);

export const articleImpactCircularityResource = () => (
  <Resource
    name="articleImpactCircularity"
    options={{ label: "Article Circularity Records (Advanced)", menuGroup: "Article Impact Sub", hiddenFromMenu: true }}
    list={ArticleCircularityList}
    show={ArticleCircularityShow}
    edit={ArticleCircularityEdit}
  />
);

export const articleImpactEcobalyseResource = () => (
  <Resource
    name="articleImpactEcobalyse"
    options={{ label: "Article Ecobalyse", menuGroup: "Article Impact Sub" }}
    list={ArticleEcobalyseList}
    show={ArticleEcobalyseShow}
    edit={ArticleEcobalyseEdit}
  />
);
